import { useId } from 'react';
import {
  Label,
  Text,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
} from 'react-aria-components';

import styles from './NoteEditor.module.css';

export interface NoteEditorSection {
  key: string;
  heading: string;
  text: string;
  /** Guidance under the heading, from the template. */
  hint?: string | null;
}

export interface NoteEditorProps {
  /** Always visible, e.g. "Note". */
  label: string;
  sections: readonly NoteEditorSection[];
  onChange: (key: string, text: string) => void;
  /** The save state in words: "Saved 14:05", "Saving…", "Saved on this device". */
  saveState?: string;
  isReadOnly?: boolean;
}

/**
 * The clinical note (DESIGN.md §8, Consultation): one long form with anchored headings,
 * not tabs, so nothing is hidden. A jump list links to each section. The note's text is
 * the clinician's; this component never trims or rewrites it.
 */
export function NoteEditor({
  label,
  sections,
  onChange,
  saveState,
  isReadOnly = false,
}: NoteEditorProps) {
  const base = useId();
  const anchor = (key: string) => `${base}-${key}`;
  return (
    <section className={styles.editor} aria-label={label}>
      <div className={styles.head}>
        <nav aria-label={`${label} sections`}>
          <ul className={styles.jump}>
            {sections.map((s) => (
              <li key={s.key}>
                <a className={styles.jumpLink} href={`#${anchor(s.key)}`}>
                  {s.heading}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        {saveState === undefined ? null : (
          <p className={styles.saveState} role="status">
            {saveState}
          </p>
        )}
      </div>
      {sections.map((s) => (
        <AriaTextField
          key={s.key}
          id={anchor(s.key)}
          className={styles.section}
          value={s.text}
          onChange={(text) => {
            onChange(s.key, text);
          }}
          isReadOnly={isReadOnly}
        >
          <Label className={styles.heading}>{s.heading}</Label>
          {s.hint === undefined || s.hint === null ? null : (
            <Text slot="description" className={styles.hint}>
              {s.hint}
            </Text>
          )}
          <AriaTextArea className={styles.text} rows={3} />
        </AriaTextField>
      ))}
    </section>
  );
}
